import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { employeesApi } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Search, Save, Loader2, ExternalLink, Percent, Award } from "lucide-react";

// Per-row dirty tracking + save. Old per-employee form on /staff stays as-is —
// this is a parallel view for side-by-side editing across all managers.
const FIELDS = [
  "crm_bonus_percent",
  "bonus",
  "art_director_bonus_percent",
  "pm_profit_bonus_percent",
  "sales_bonus_percent",
  "override_bonus_rate_low",
  "override_bonus_rate_high",
  "is_partnership",
];

// Same name-based identity check as backend (server.py, frontend SalesDashboardPage).
// If the commercial-director role ever transfers, update all three sites.
const isOverrideRecipient = (emp) => {
  const n = (emp?.name || "").toLowerCase();
  return n.includes("kuzmenko") && n.includes("kostiantyn");
};

const numericChanged = (a, b) => Number(a || 0) !== Number(b || 0);

const BonusMatrixPage = () => {
  const { formatAmount } = useCurrency();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // {employee_id: {field: value}}
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("sales");

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const { data } = await employeesApi.getAll(true);
      setEmployees(data || []);
    } catch (err) {
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const getValue = (emp, field) => {
    const draft = drafts[emp.id];
    if (draft && Object.prototype.hasOwnProperty.call(draft, field)) return draft[field];
    return emp[field];
  };

  const isRowDirty = (emp) => {
    const draft = drafts[emp.id];
    if (!draft) return false;
    return FIELDS.some((f) => {
      if (!Object.prototype.hasOwnProperty.call(draft, f)) return false;
      if (f === "is_partnership") return Boolean(draft[f]) !== Boolean(emp[f]);
      return numericChanged(draft[f], emp[f]);
    });
  };

  const setDraft = (id, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value },
    }));
  };

  const resetRow = (id) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const saveRow = async (emp) => {
    const draft = drafts[emp.id];
    if (!draft) return;
    setSavingId(emp.id);
    try {
      // Send full employee shape — backend's PUT /employees/{id} accepts a model
      // and ignores fields it doesn't know. Pre-existing flow uses the same
      // pattern (StaffPage sends formData diff merged into base).
      const payload = {};
      FIELDS.forEach((f) => {
        if (!Object.prototype.hasOwnProperty.call(draft, f)) return;
        if (f === "is_partnership") {
          payload[f] = Boolean(draft[f]);
        } else if (
          f === "pm_profit_bonus_percent"
          || f === "sales_bonus_percent"
          || f === "override_bonus_rate_low"
          || f === "override_bonus_rate_high"
        ) {
          // empty input → null. pm_profit_bonus_percent: falls back to 0.75% default.
          // sales_bonus_percent: no bonus generated. Explicit 0 = 0% (also no bonus).
          // override_bonus_rate_low/high: fall back to 4 / 6 in backend.
          const raw = draft[f];
          payload[f] = raw === "" || raw === null || raw === undefined ? null : parseFloat(raw);
          if (Number.isNaN(payload[f])) payload[f] = null;
        } else {
          payload[f] = draft[f] === "" ? 0 : parseFloat(draft[f]) || 0;
        }
      });
      const { data } = await employeesApi.update(emp.id, payload);
      setEmployees((prev) => prev.map((e) => (e.id === emp.id ? { ...e, ...data } : e)));
      resetRow(emp.id);
      toast.success(`Saved ${emp.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to save ${emp.name}`);
    } finally {
      setSavingId(null);
    }
  };

  const dirtyCount = useMemo(
    () => employees.filter((e) => isRowDirty(e)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employees, drafts],
  );

  const saveAll = async () => {
    const dirty = employees.filter((e) => isRowDirty(e));
    for (const emp of dirty) {
      // Sequential to keep error toasts readable; tiny lists (<30 rows).
      // eslint-disable-next-line no-await-in-loop
      await saveRow(emp);
    }
  };

  const filterByTab = (emp) => {
    if (tab === "sales") return emp.position === "sales";
    if (tab === "art_director") return emp.position === "art_director";
    if (tab === "pm") return emp.position === "project_manager";
    return true;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees
      .filter(filterByTab)
      .filter((e) => !q || e.name?.toLowerCase().includes(q) || e.email?.toLowerCase().includes(q))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, search, tab]);

  const totals = useMemo(() => {
    if (tab === "sales") {
      const withSalesBonus = filtered.filter((e) => {
        const v = getValue(e, "sales_bonus_percent");
        return v !== null && v !== undefined && v !== "" && Number(v) > 0;
      });
      return {
        avgCrmBonus: filtered.length
          ? filtered.reduce((sum, e) => sum + Number(getValue(e, "crm_bonus_percent") || 0), 0) / filtered.length
          : 0,
        salesBonusCount: withSalesBonus.length,
        partnershipCount: filtered.filter((e) => Boolean(getValue(e, "is_partnership"))).length,
      };
    }
    if (tab === "art_director") {
      return {
        avgAdBonus: filtered.length
          ? filtered.reduce((sum, e) => sum + Number(getValue(e, "art_director_bonus_percent") || 0), 0) / filtered.length
          : 0,
      };
    }
    if (tab === "pm") {
      return {
        avgPmBonus: filtered.length
          ? filtered.reduce(
              (sum, e) => sum + (Number(getValue(e, "pm_profit_bonus_percent") ?? 0.75) || 0),
              0,
            ) / filtered.length
          : 0,
        sumFixed: filtered.reduce((sum, e) => sum + Number(getValue(e, "bonus") || 0), 0),
      };
    }
    return {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, drafts]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Percent className="h-6 w-6 text-orange-400" />
              Bonus Matrix
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Side-by-side editing of bonus settings across all managers. Per-employee form remains on{" "}
              <Link to="/staff" className="text-orange-400 hover:text-orange-300 inline-flex items-center gap-1">
                /staff <ExternalLink className="h-3 w-3" />
              </Link>
              .
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dirtyCount > 0 && (
              <Button
                onClick={saveAll}
                disabled={Boolean(savingId)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {savingId ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save all ({dirtyCount})
              </Button>
            )}
          </div>
        </div>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-zinc-500" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or email…"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 pl-8"
                />
              </div>
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-zinc-800 border border-zinc-700">
                  <TabsTrigger value="sales" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-300">Sales</TabsTrigger>
                  <TabsTrigger value="art_director" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-300">Art Directors</TabsTrigger>
                  <TabsTrigger value="pm" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-300">PM</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Stats strip */}
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge variant="outline" className="bg-zinc-800/50 border-zinc-700 text-zinc-300 font-mono">
                {filtered.length} {tab === "sales" ? "sales managers" : tab === "art_director" ? "art directors" : "project managers"}
              </Badge>
              {tab === "sales" && (
                <>
                  <Badge variant="outline" className="bg-orange-500/10 border-orange-500/30 text-orange-300 font-mono">
                    avg CRM bonus: {totals.avgCrmBonus?.toFixed(2)}%
                  </Badge>
                  <Badge variant="outline" className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 font-mono">
                    sales bonus configured: {totals.salesBonusCount}/{filtered.length}
                  </Badge>
                  <Badge variant="outline" className="bg-purple-500/10 border-purple-500/30 text-purple-300 font-mono">
                    partnership: {totals.partnershipCount}
                  </Badge>
                </>
              )}
              {tab === "art_director" && (
                <Badge variant="outline" className="bg-orange-500/10 border-orange-500/30 text-orange-300 font-mono">
                  avg AD bonus: {totals.avgAdBonus?.toFixed(2)}%
                </Badge>
              )}
              {tab === "pm" && (
                <>
                  <Badge variant="outline" className="bg-orange-500/10 border-orange-500/30 text-orange-300 font-mono">
                    avg PM profit bonus: {totals.avgPmBonus?.toFixed(2)}%
                  </Badge>
                  <Badge variant="outline" className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 font-mono">
                    fixed bonus pool: {formatAmount(totals.sumFixed || 0, "USD")}
                  </Badge>
                </>
              )}
            </div>

            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                No active {tab === "sales" ? "sales managers" : tab === "art_director" ? "art directors" : "project managers"} match the filter.
              </div>
            ) : (
              <div className="overflow-x-auto rounded border border-zinc-800">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="w-10 text-zinc-500">#</TableHead>
                      <TableHead className="text-zinc-400">Employee</TableHead>
                      <TableHead className="text-zinc-400 w-32">Salary</TableHead>
                      {tab === "sales" && (
                        <>
                          <TableHead className="text-zinc-400 w-32">CRM Bonus %</TableHead>
                          <TableHead className="text-zinc-400 w-40">Sales Bonus %</TableHead>
                          <TableHead className="text-zinc-400 w-32">Partnership</TableHead>
                        </>
                      )}
                      {tab === "art_director" && (
                        <TableHead className="text-zinc-400 w-32">AD Bonus %</TableHead>
                      )}
                      {tab === "pm" && (
                        <>
                          <TableHead className="text-zinc-400 w-44">PM Profit Bonus %</TableHead>
                          <TableHead className="text-zinc-400 w-32">Bonus (fix)</TableHead>
                        </>
                      )}
                      <TableHead className="text-zinc-400 w-28 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((emp, idx) => {
                      const dirty = isRowDirty(emp);
                      return (
                        <TableRow
                          key={emp.id}
                          className={cn("border-zinc-800", dirty && "bg-orange-500/5")}
                        >
                          <TableCell className="text-zinc-500 font-mono">{idx + 1}</TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="text-zinc-50 font-medium">{emp.name}</span>
                              <span className="text-xs text-zinc-500">{emp.email || "—"}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-zinc-300 font-mono text-sm">
                            {formatAmount(emp.salary || 0, emp.currency || "USD")}
                          </TableCell>

                          {tab === "sales" && (
                            <>
                              <TableCell>
                                <div className="relative">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={getValue(emp, "crm_bonus_percent") ?? 0}
                                    onChange={(e) => setDraft(emp.id, "crm_bonus_percent", e.target.value)}
                                    className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm font-mono pr-7 h-8"
                                  />
                                  <span className="absolute right-2 top-1.5 text-zinc-500 text-xs">%</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="relative">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    placeholder="—"
                                    value={getValue(emp, "sales_bonus_percent") ?? ""}
                                    onChange={(e) => setDraft(emp.id, "sales_bonus_percent", e.target.value)}
                                    className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm font-mono pr-7 h-8"
                                  />
                                  <span className="absolute right-2 top-1.5 text-zinc-500 text-xs">%</span>
                                </div>
                                {(getValue(emp, "sales_bonus_percent") === null
                                  || getValue(emp, "sales_bonus_percent") === undefined
                                  || getValue(emp, "sales_bonus_percent") === ""
                                  || Number(getValue(emp, "sales_bonus_percent")) === 0) && (
                                  <span className="text-[10px] text-zinc-600">empty = no sales bonus</span>
                                )}
                                {isOverrideRecipient(emp) && (
                                  <div className="mt-2 pt-2 border-t border-zinc-800 space-y-1.5">
                                    <div className="text-[10px] text-amber-400 font-medium uppercase tracking-wide">
                                      Override on peer sales
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="relative w-16">
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          max="100"
                                          placeholder="4"
                                          value={getValue(emp, "override_bonus_rate_low") ?? ""}
                                          onChange={(e) => setDraft(emp.id, "override_bonus_rate_low", e.target.value)}
                                          className="bg-zinc-950 border-zinc-800 text-zinc-50 text-xs font-mono pr-6 h-7"
                                        />
                                        <span className="absolute right-1.5 top-1 text-zinc-500 text-[10px]">%</span>
                                      </div>
                                      <span className="text-[10px] text-zinc-500">@ ≥ $10k peers</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="relative w-16">
                                        <Input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          max="100"
                                          placeholder="6"
                                          value={getValue(emp, "override_bonus_rate_high") ?? ""}
                                          onChange={(e) => setDraft(emp.id, "override_bonus_rate_high", e.target.value)}
                                          className="bg-zinc-950 border-zinc-800 text-zinc-50 text-xs font-mono pr-6 h-7"
                                        />
                                        <span className="absolute right-1.5 top-1 text-zinc-500 text-[10px]">%</span>
                                      </div>
                                      <span className="text-[10px] text-zinc-500">@ ≥ $50k peers</span>
                                    </div>
                                    <div className="text-[10px] text-zinc-600">
                                      empty = default 4% / 6%
                                    </div>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={Boolean(getValue(emp, "is_partnership"))}
                                    onCheckedChange={(v) => setDraft(emp.id, "is_partnership", v)}
                                  />
                                  {Boolean(getValue(emp, "is_partnership")) && (
                                    <Award className="h-3.5 w-3.5 text-purple-400" />
                                  )}
                                </div>
                              </TableCell>
                            </>
                          )}

                          {tab === "art_director" && (
                            <TableCell>
                              <div className="relative">
                                <Input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  value={getValue(emp, "art_director_bonus_percent") ?? 0}
                                  onChange={(e) => setDraft(emp.id, "art_director_bonus_percent", e.target.value)}
                                  className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm font-mono pr-7 h-8"
                                />
                                <span className="absolute right-2 top-1.5 text-zinc-500 text-xs">%</span>
                              </div>
                            </TableCell>
                          )}

                          {tab === "pm" && (
                            <>
                              <TableCell>
                                <div className="relative">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    placeholder="0.75"
                                    value={getValue(emp, "pm_profit_bonus_percent") ?? ""}
                                    onChange={(e) => setDraft(emp.id, "pm_profit_bonus_percent", e.target.value)}
                                    className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm font-mono pr-7 h-8"
                                  />
                                  <span className="absolute right-2 top-1.5 text-zinc-500 text-xs">%</span>
                                </div>
                                {(getValue(emp, "pm_profit_bonus_percent") === null
                                  || getValue(emp, "pm_profit_bonus_percent") === undefined
                                  || getValue(emp, "pm_profit_bonus_percent") === "") && (
                                  <span className="text-[10px] text-zinc-600">defaults to 0.75% of profit</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="relative">
                                  <span className="absolute left-2 top-1.5 text-zinc-500 text-xs">$</span>
                                  <Input
                                    type="number"
                                    step="1"
                                    min="0"
                                    value={getValue(emp, "bonus") ?? 0}
                                    onChange={(e) => setDraft(emp.id, "bonus", e.target.value)}
                                    className="bg-zinc-950 border-zinc-800 text-zinc-50 text-sm font-mono pl-6 h-8"
                                  />
                                </div>
                              </TableCell>
                            </>
                          )}

                          <TableCell className="text-right">
                            {dirty ? (
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs border-zinc-700"
                                  onClick={() => resetRow(emp.id)}
                                  disabled={Boolean(savingId)}
                                >
                                  Reset
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-7 text-xs bg-orange-500 hover:bg-orange-600"
                                  onClick={() => saveRow(emp)}
                                  disabled={Boolean(savingId)}
                                >
                                  {savingId === emp.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <Save className="h-3 w-3" />
                                  )}
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-zinc-600">saved</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BonusMatrixPage;
